import { useState, useEffect } from 'react';
import { baserowAPI, Company } from '../services/baserowAPI';

// Cache global pour éviter les appels API répétés
let companiesCache: Company[] | null = null;
let companiesCachePromise: Promise<Company[]> | null = null;

// Listeners pour notifier les composants quand le cache est invalidé
let cacheInvalidationListeners: (() => void)[] = [];

// Fonction pour ajouter un listener
function addCacheInvalidationListener(callback: () => void) {
  cacheInvalidationListeners.push(callback);
  return () => {
    cacheInvalidationListeners = cacheInvalidationListeners.filter(cb => cb !== callback);
  };
}

// Fonction pour notifier tous les listeners
function notifyCacheInvalidation() {
  cacheInvalidationListeners.forEach(callback => callback());
}

// Fonction pour réinitialiser le cache
export function invalidateCompaniesCache() {
  companiesCache = null;
  companiesCachePromise = null;
  notifyCacheInvalidation();
}

export function useCompanies() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    const loadCompanies = async () => {
      try {
        setLoading(true);
        setError(null);

        // Si les entreprises sont déjà en cache, les utiliser
        if (companiesCache) {
          setCompanies(companiesCache);
          return;
        }

        // Si une requête est en cours, attendre son résultat
        if (companiesCachePromise) {
          const cachedCompanies = await companiesCachePromise;
          setCompanies(cachedCompanies);
          return;
        }

        // Sinon, faire la requête
        companiesCachePromise = baserowAPI.getAllCompanies();
        const loadedCompanies = await companiesCachePromise;
        
        // Mettre en cache et afficher
        companiesCache = loadedCompanies;
        setCompanies(loadedCompanies);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Erreur lors du chargement des entreprises';
        setError(errorMessage);
        console.error('Erreur useCompanies:', err);
      } finally {
        setLoading(false);
      }
    };

    loadCompanies();
  }, [refreshTrigger]);

  // Écouter les invalidations du cache
  useEffect(() => {
    const unsubscribe = addCacheInvalidationListener(() => {
      setRefreshTrigger(prev => prev + 1);
    });
    return unsubscribe;
  }, []);

  return { companies, loading, error };
}
